import { useEffect, useSyncExternalStore } from "react";
import locationApi from "../utilities/locationApi";
import { getLieuxPage, toutesLesQuetes } from "../utilities/locationService";
import { getFileRetraits } from "../utilities/userService";
import { Niveau } from "../components/charte/TroisBarres";

/* ══════════════════════════════════════════════════════════════════════════
   Décisions en attente, comptées UNIQUEMENT avec des appels existants :
   - lieux à valider : GET /location/backoffice/lieux (getLieuxPage), onglets
     « attente » et « verifie » ; le plus ancien est lu sur la dernière page
     (la file est triée du plus récent au plus ancien) ;
   - quêtes à valider : GET /quest/all (toutesLesQuetes), statut
     en_attente_validation (demandes clients comprises, source client_ia) ;
   - réponses aux quêtes : GET /quest/{id}/submissions (appel du détail de
     quête), pour les seules quêtes qui ont reçu au moins une collecte
     (collectesRealisees > 0) — il n'existe pas d'appel global ;
   - retraits : GET /token/file (getFileRetraits), à décider et à verser.
   Un appel refusé (droits, réseau) masque la ligne sans bloquer les autres.
   Mémoire partagée par le menu, l'Accueil et À traiter ; rechargée au plus
   toutes les 60 s, ou tout de suite après une décision (invaliderAttente).
   ══════════════════════════════════════════════════════════════════════════ */

export type CleAttente = "lieux" | "reponses" | "quetes" | "retraits";

export interface LigneAttente {
  nombre: number;
  /** Date de l'élément le plus ancien ; null si l'appel ne la donne pas. */
  plusAncien: Date | null;
  /** Précision affichée sous le titre (ex. « dont 2 demandes clients »). */
  detail?: string;
}

export interface EtatAttente {
  lignes: Partial<Record<CleAttente, LigneAttente>>;
  /** Toutes les quêtes (cycles compris), réutilisées par l'Accueil. */
  quetes: any[];
  /** Soumissions lues pour compter les réponses (toutes statuts), par l'Accueil. */
  soumissions: any[];
  charge: boolean;
}

/** Date d'un champ serveur : ISO, epoch (s ou ms) ou tableau LocalDateTime. */
export function lireDate(v: any): Date | null {
  if (v == null || v === "") return null;
  if (Array.isArray(v)) {
    const [a, mo = 1, j = 1, h = 0, mi = 0, s = 0] = v.map(Number);
    const d = new Date(a, mo - 1, j, h, mi, s);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "number") return new Date(v < 1e12 ? v * 1000 : v);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/** Urgence à partir de l'ancienneté du plus ancien : < 2 j = 1 barre,
    2 à 7 j = 2 barres, > 7 j = 3 barres ; 1 barre si l'ancienneté est inconnue. */
export function niveauUrgence(plusAncien: Date | null): Niveau {
  if (!plusAncien) return 1;
  const jours = (Date.now() - plusAncien.getTime()) / 86400000;
  return jours < 2 ? 1 : jours <= 7 ? 2 : 3;
}

const plusAncienDe = (dates: (Date | null)[]): Date | null =>
  dates.filter((d): d is Date => !!d).sort((a, b) => a.getTime() - b.getTime())[0] ?? null;

// ── Chargeurs ───────────────────────────────────────────────────────────────

async function chargerLieux(): Promise<LigneAttente> {
  const tete = await getLieuxPage({ onglet: "attente", page: 0, taille: 1 });
  const c = tete.compteurs ?? ({} as any);
  const nbAttente = Number(c.attente) || 0;
  const nbVerifie = Number(c.verifie) || 0;
  const dernier = async (onglet: "attente" | "verifie", total: number) => {
    if (total <= 0) return null;
    const p = await getLieuxPage({ onglet, page: total - 1, taille: 1 });
    return lireDate(p.contenu?.[0]?.createdOn);
  };
  const [a, v] = await Promise.all([dernier("attente", nbAttente), dernier("verifie", nbVerifie)]);
  return {
    nombre: nbAttente + nbVerifie,
    plusAncien: plusAncienDe([a, v]),
    detail: nbVerifie > 0 ? `dont ${nbVerifie} déjà corroboré${nbVerifie > 1 ? "s" : ""} par un 2e Spotter` : undefined,
  };
}

function ligneQuetes(quetes: any[]): LigneAttente {
  const aValider = quetes.filter(q => (q.statut ?? "").toLowerCase() === "en_attente_validation");
  const clients = aValider.filter(q => q.source === "client_ia").length;
  return {
    nombre: aValider.length,
    plusAncien: plusAncienDe(aValider.map(q => lireDate(q.createdAt))),
    detail: clients > 0 ? `dont ${clients} demande${clients > 1 ? "s" : ""} client${clients > 1 ? "s" : ""}` : undefined,
  };
}

/** Soumissions des quêtes ayant reçu au moins une collecte, 6 appels à la fois. */
async function chargerSoumissions(quetes: any[]): Promise<any[]> {
  const cibles = quetes.filter(q => (Number(q.collectesRealisees) || 0) > 0 && q.id);
  const toutes: any[] = [];
  for (let i = 0; i < cibles.length; i += 6) {
    const lot = await Promise.allSettled(cibles.slice(i, i + 6).map(q => locationApi.get(`/quest/${q.id}/submissions`)));
    lot.forEach((r, j) => {
      if (r.status !== "fulfilled" || !Array.isArray(r.value.data)) return;
      const q = cibles[i + j];
      r.value.data.forEach((s: any) => toutes.push({ ...s, questId: s.questId ?? q.id, questTitre: s.questTitre ?? q.titre }));
    });
  }
  return toutes;
}

export const estEnAttente = (s: any) => (s?.statut || "en_attente").toLowerCase() === "en_attente";

function ligneReponses(soumissions: any[]): LigneAttente {
  const attente = soumissions.filter(estEnAttente);
  const nbQuetes = new Set(attente.map(s => s.questId)).size;
  return {
    nombre: attente.length,
    plusAncien: plusAncienDe(attente.map(s => lireDate(s.createdAt))),
    detail: attente.length > 0 ? `sur ${nbQuetes} quête${nbQuetes > 1 ? "s" : ""}` : undefined,
  };
}

async function chargerRetraits(): Promise<LigneAttente> {
  const file = await getFileRetraits();
  const aDecider = file.filter((r: any) => r.status === "pending" || r.status === "premiere_approbation").length;
  const aVerser = file.filter((r: any) => r.status === "approved").length;
  const morceaux = [aDecider > 0 ? `${aDecider} à décider` : "", aVerser > 0 ? `${aVerser} à verser` : ""].filter(Boolean);
  return {
    nombre: file.length,
    plusAncien: plusAncienDe(file.map((r: any) => lireDate(r.used_on))),
    detail: morceaux.join(", ") || undefined,
  };
}

// ── Mémoire partagée ────────────────────────────────────────────────────────

let etat: EtatAttente = { lignes: {}, quetes: [], soumissions: [], charge: false };
let dernierChargement = 0;
let enCours: Promise<void> | null = null;
const abonnes = new Set<() => void>();

const publier = (maj: Partial<EtatAttente>) => {
  etat = { ...etat, ...maj, lignes: { ...etat.lignes, ...(maj.lignes ?? {}) } };
  abonnes.forEach(f => f());
};

export function chargerAttente(force = false): Promise<void> {
  if (sessionStorage.getItem("isLoggedIn") !== "true" || !sessionStorage.getItem("jwtToken")) return Promise.resolve();
  if (enCours) return enCours;
  if (!force && etat.charge && Date.now() - dernierChargement < 60000) return Promise.resolve();
  dernierChargement = Date.now();
  enCours = (async () => {
    const retirer = (cle: CleAttente) => { const l = { ...etat.lignes }; delete l[cle]; etat = { ...etat, lignes: l }; };
    const lieux = chargerLieux().then(l => publier({ lignes: { lieux: l } })).catch(() => retirer("lieux"));
    const retraits = chargerRetraits().then(l => publier({ lignes: { retraits: l } })).catch(() => retirer("retraits"));
    const quetes = toutesLesQuetes(false).then(async qs => {
      publier({ quetes: qs, lignes: { quetes: ligneQuetes(qs) } });
      const soumissions = await chargerSoumissions(qs);
      publier({ soumissions, lignes: { reponses: ligneReponses(soumissions) } });
    }).catch(() => { retirer("quetes"); retirer("reponses"); });
    await Promise.all([lieux, retraits, quetes]);
    publier({ charge: true });
  })().finally(() => { enCours = null; });
  return enCours;
}

/** À appeler après une décision : compteurs relus au prochain affichage. */
export function invaliderAttente() {
  dernierChargement = 0;
  chargerAttente(true);
}

export function useAttente(rafraichirAuMontage = false): EtatAttente {
  const lu = useSyncExternalStore(
    (f) => { abonnes.add(f); return () => { abonnes.delete(f); }; },
    () => etat,
  );
  useEffect(() => { chargerAttente(rafraichirAuMontage); }, []);
  return lu;
}

/** Total affiché sur l'entrée « À traiter » du menu. */
export function totalAttente(e: EtatAttente): number {
  return (["lieux", "reponses", "quetes", "retraits"] as CleAttente[])
    .reduce((t, k) => t + (e.lignes[k]?.nombre ?? 0), 0);
}

/** À la déconnexion : rien ne reste d'une session à l'autre. */
export function viderAttente() {
  dernierChargement = 0;
  publier({ lignes: {}, quetes: [], soumissions: [], charge: false });
  etat = { lignes: {}, quetes: [], soumissions: [], charge: false };
}
